/**
 * ModalShell — общий каркас модалок карточки (шапка с заголовком+крестиком,
 * тело, ошибка, футер Отмена/действие). Убирает повтор в Add/Edit/ChangePhone.
 */

import type {ReactNode} from 'react';
import {X} from 'lucide-react';

import {Button} from '@components/ui/Button';
import {Dialog, DialogContent} from '@components/ui/Dialog';
import {cn} from '@lib/utils';

type ModalShellProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  submitLabel: string;
  onSubmit: () => void;
  /** Мутация в полёте — кнопка занята */
  pending?: boolean;
  /** Форма невалидна — кнопка заперта */
  submitDisabled?: boolean;
  /** Текст ошибки сабмита под телом */
  error?: string | null;
  /** Опасное действие — красная кнопка */
  danger?: boolean;
  /** Спрятать «Отмена» (напр. экран успеха с одной кнопкой) */
  hideCancel?: boolean;
  width?: number;
  children: ReactNode;
};

export const ModalShell = ({
  open,
  onClose,
  title,
  subtitle,
  submitLabel,
  onSubmit,
  pending = false,
  submitDisabled = false,
  error = null,
  danger = false,
  hideCancel = false,
  width = 420,
  children,
}: ModalShellProps) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent
      showClose={false}
      style={{width}}
      className="max-h-[88vh] max-w-[calc(100vw-2rem)] gap-0 overflow-y-auto rounded-[18px] p-[22px]"
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-[17px] font-bold">{title}</h3>
        <button
          onClick={onClose}
          aria-label="Закрыть"
          className="grid size-[30px] place-items-center rounded-lg bg-secondary text-muted-foreground"
        >
          <X className="size-4" strokeWidth={2} />
        </button>
      </div>

      {subtitle && (
        <div className="mb-[18px] text-[12.5px] leading-[1.5] text-muted-foreground">
          {subtitle}
        </div>
      )}

      <div className="flex flex-col gap-3.5">{children}</div>

      {error !== null && (
        <div className="mt-3.5 rounded-[10px] bg-destructive/10 px-3.5 py-2.5 text-[12.5px] font-medium text-destructive">
          {error}
        </div>
      )}

      <div className="mt-[22px] flex gap-2.5">
        {!hideCancel && (
          <Button variant="outline" className="h-11 flex-1 rounded-[10px]" onClick={onClose}>
            Отмена
          </Button>
        )}
        <Button
          className={cn(
            'h-11 flex-1 rounded-[10px]',
            danger && 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
          )}
          disabled={pending || submitDisabled}
          onClick={onSubmit}
        >
          {pending ? 'Сохраняем…' : submitLabel}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
